import { supabase } from './supabase';
import { toast } from 'sonner';

export type DocumentStatusEmailItem = {
    title?: string | null;
    documentType?: string | null;
    /** Per-file outcome; falls back to the top-level status when omitted. */
    status?: 'approved' | 'rejected' | null;
};

export type NotifyDocumentStatusOptions = {
    tenantName?: string | null;
    documents?: DocumentStatusEmailItem[];
    /** Shown on rejection emails when the admin provided a reason. */
    rejectionNotes?: string | null;
    /** Prefer passing this: edge function loads title/type from DB so the email always names the file. */
    documentId?: string | null;
};

/** Sends tenant-facing email after document approval/rejection (edge function; admin-only). */
export async function notifyTenantDocumentStatusEmail(
    tenantUserId: string | null | undefined,
    status: 'approved' | 'rejected',
    options?: NotifyDocumentStatusOptions
): Promise<void> {
    if (!tenantUserId) return;
    try {
        const { data, error } = await supabase.functions.invoke('send-tenant-document-status-email', {
            body: {
                tenantUserId,
                status,
                tenantName: options?.tenantName?.trim() || undefined,
                documentId: options?.documentId?.trim() || undefined,
                documents: options?.documents?.length
                    ? options.documents.map((d) => ({
                          title: d.title,
                          documentType: d.documentType,
                          status: d.status ?? undefined,
                      }))
                    : undefined,
                rejectionNotes: options?.rejectionNotes?.trim() || undefined,
            },
        });
        if (error) {
            console.error('send-tenant-document-status-email:', error);
            toast.warning('Document updated, but the notification email could not be sent.');
            return;
        }
        const d = data as { success?: boolean; skipped?: string; message?: string };
        if (d?.skipped === 'no_tenant_email') {
            toast.warning('Tenant has no email on file; notification was skipped.');
        } else if (d?.skipped === 'no_resend_key') {
            // Local dev without Resend — silent
        }
    } catch (e) {
        console.error(e);
        toast.warning('Document updated, but the notification email could not be sent.');
    }
}
